import React, { useEffect, useMemo, useState } from 'react';
import { Box, Button, Card, CardContent, Grid, Stack, Typography } from '@mui/material';
import dayjs from 'dayjs';
import { useClient } from '../core/client';
import { useNavigate } from 'react-router-dom';

const RANGE_OPTIONS = [7, 14, 30];

const InteractiveLineChart = ({ labels, values, color }) => {
    const [hoverIndex, setHoverIndex] = useState(null);
    const width = 1000;
    const height = 360;
    const padX = 40;
    const padY = 30;
    const usableWidth = width - padX * 2;
    const usableHeight = height - padY * 2;
    const min = Math.min(...values, 0);
    const max = Math.max(...values, 1);
    const range = Math.max(max - min, 1);
    const count = values.length;

    const toPoint = (index, value) => {
        const x = count > 1 ? padX + (index / (count - 1)) * usableWidth : width / 2;
        const y = padY + (1 - (value - min) / range) * usableHeight;
        return { x, y };
    };

    const points = values.map((value, index) => toPoint(index, value));
    const pathData = points.map((p, index) => `${index === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ');
    const areaData = `${pathData} L ${padX + usableWidth} ${padY + usableHeight} L ${padX} ${padY + usableHeight} Z`;
    const hoverPoint = hoverIndex !== null ? points[hoverIndex] : null;

    const onMouseMove = (event) => {
        const rect = event.currentTarget.getBoundingClientRect();
        const x = ((event.clientX - rect.left) / rect.width) * width;
        const clamped = Math.min(Math.max(x, padX), padX + usableWidth) - padX;
        const idx = count > 1 ? Math.round((clamped / usableWidth) * (count - 1)) : 0;
        setHoverIndex(Math.max(0, Math.min(count - 1, idx)));
    };

    return (
        <Box sx={{ width: '100%', mt: 2 }}>
            <svg
                width="100%"
                viewBox={`0 0 ${width} ${height}`}
                onMouseMove={onMouseMove}
                onMouseLeave={() => setHoverIndex(null)}
                style={{ display: 'block' }}
            >
                <defs>
                    <linearGradient id="chart-fill" x1="0" x2="0" y1="0" y2="1">
                        <stop offset="0%" stopColor={color} stopOpacity="0.28" />
                        <stop offset="100%" stopColor={color} stopOpacity="0.02" />
                    </linearGradient>
                </defs>
                <line x1={padX} y1={padY + usableHeight} x2={padX + usableWidth} y2={padY + usableHeight} stroke="rgba(255,255,255,0.25)" />
                <path d={areaData} fill="url(#chart-fill)" />
                <path d={pathData} fill="none" stroke={color} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
                {hoverPoint && (
                    <>
                        <line x1={hoverPoint.x} y1={padY} x2={hoverPoint.x} y2={padY + usableHeight} stroke="rgba(255,255,255,0.35)" strokeDasharray="6 6" />
                        <circle cx={hoverPoint.x} cy={hoverPoint.y} r="5.5" fill={color} />
                    </>
                )}
            </svg>
            {hoverIndex !== null && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    {dayjs(labels[hoverIndex]).format('MMM D, YYYY')}: {values[hoverIndex]}
                </Typography>
            )}
        </Box>
    );
};

const DashboardDetailPage = ({ metric }) => {
    const client = useClient();
    const navigate = useNavigate();
    const [stats, setStats] = useState(null);
    const [rangeDays, setRangeDays] = useState(30);
    const [error, setError] = useState('');

    useEffect(() => {
        let mounted = true;
        const load = async () => {
            setError('');
            try {
                const { data } = await client.get('/defendants/stats');
                if (mounted) {
                    setStats(data);
                }
            } catch (e) {
                if (mounted) {
                    setError('Unable to load chart details.');
                }
            }
        };
        load();
        return () => {
            mounted = false;
        };
    }, [client]);

    const detail = useMemo(() => {
        const labels = Array.isArray(stats?.trendLabels30d) ? stats.trendLabels30d : [];
        const map = {
            total: {
                title: 'Total Defendants Details',
                color: '#14b8a6',
                values: Array.isArray(stats?.defendantsAddedTrend30d) ? stats.defendantsAddedTrend30d : [],
                summary: `Active: ${stats?.activeCases ?? 0} | Inactive: ${Math.max((stats?.totalDefendants ?? 0) - (stats?.activeCases ?? 0), 0)}`,
                subtitle: 'Daily defendants added',
                sideLabel: 'Daily Added',
                sideValue: stats?.defendantsAddedToday ?? 0,
                sideSecondaryLabel: 'Current Total',
                sideSecondaryValue: stats?.totalDefendants ?? 0,
                helpText: 'This chart tracks how many defendants were added each day.',
                interpretation: 'Higher values mean faster caseload growth over the selected period.',
            },
            missed: {
                title: 'Missed Check-ins Details',
                color: '#f59e0b',
                values: Array.isArray(stats?.missedTrend30d) ? stats.missedTrend30d : [],
                summary: `Total missed check-ins: ${stats?.missedCheckins ?? 0}`,
                subtitle: 'Daily missed check-ins',
                sideLabel: 'Missed Today',
                sideValue: stats?.missedToday ?? 0,
                sideSecondaryLabel: 'Total Missed',
                sideSecondaryValue: stats?.missedCheckins ?? 0,
                helpText: 'This chart shows missed check-ins per day.',
                interpretation: 'Lower is better. Look for downtrends and spikes that need follow-up.',
            },
            completed: {
                title: 'Completed Today Details',
                color: '#22c55e',
                values: Array.isArray(stats?.completedTrend30d) ? stats.completedTrend30d : [],
                summary: `Completed today: ${stats?.completedToday ?? 0} of ${stats?.todayTotalCheckins ?? 0}`,
                subtitle: 'Daily completed check-ins',
                sideLabel: 'Completed Today',
                sideValue: stats?.completedToday ?? 0,
                sideSecondaryLabel: 'Scheduled Today',
                sideSecondaryValue: stats?.todayTotalCheckins ?? 0,
                helpText: 'This chart tracks completed check-ins by day.',
                interpretation: 'Higher values indicate better daily completion performance.',
            },
        };
        const selected = map[metric] ?? map.total;
        const take = Math.min(rangeDays, selected.values.length, labels.length);
        return {
            ...selected,
            labels: labels.slice(-take),
            values: selected.values.slice(-take),
        };
    }, [metric, rangeDays, stats]);

    return (
        <Box>
            <Button variant="outlined" onClick={() => navigate('/')} sx={{ mb: 2 }}>
                Back to Dashboard
            </Button>
            <Typography variant="h4" sx={{ mb: 2 }}>{detail.title}</Typography>
            {error && <Typography color="error" sx={{ mb: 2 }}>{error}</Typography>}
            <Grid container spacing={2}>
                <Grid item xs={12} lg={9}>
                    <Card>
                        <CardContent>
                            <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
                                <Box>
                                    <Typography variant="h6">{detail.subtitle}</Typography>
                                    <Typography variant="body2" color="text.secondary">{detail.summary}</Typography>
                                </Box>
                                <Stack direction="row" spacing={1}>
                                    {RANGE_OPTIONS.map((days) => (
                                        <Button
                                            key={days}
                                            variant={rangeDays === days ? 'contained' : 'outlined'}
                                            onClick={() => setRangeDays(days)}
                                        >
                                            {days}D
                                        </Button>
                                    ))}
                                </Stack>
                            </Stack>
                            {detail.values.length > 0 ? (
                                <InteractiveLineChart labels={detail.labels} values={detail.values} color={detail.color} />
                            ) : (
                                <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                                    No trend data available.
                                </Typography>
                            )}
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} lg={3}>
                    <Card sx={{ height: '100%' }}>
                        <CardContent>
                            <Typography variant="h6" sx={{ mb: 1.5 }}>Details</Typography>
                            <Stack spacing={1.25}>
                                <Typography variant="body2" color="text.secondary">
                                    <Box component="span" sx={{ color: detail.color, fontWeight: 700 }}>Legend:</Box> {detail.sideLabel}
                                </Typography>
                                <Typography variant="body2">
                                    <Box component="span" sx={{ color: 'text.secondary' }}>{detail.sideLabel}: </Box>
                                    <Box component="span" sx={{ fontWeight: 700 }}>{detail.sideValue}</Box>
                                </Typography>
                                <Typography variant="body2">
                                    <Box component="span" sx={{ color: 'text.secondary' }}>{detail.sideSecondaryLabel}: </Box>
                                    <Box component="span" sx={{ fontWeight: 700 }}>{detail.sideSecondaryValue}</Box>
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ pt: 1 }}>
                                    {detail.helpText}
                                </Typography>
                            </Stack>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12}>
                    <Card>
                        <CardContent>
                            <Typography variant="h6" sx={{ mb: 1 }}>How To Read This Chart</Typography>
                            <Typography variant="body2" color="text.secondary">
                                Hover over the line to inspect exact daily values. Use 7D, 14D, or 30D to change the time window.
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                {detail.interpretation}
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>
        </Box>
    );
};

export default DashboardDetailPage;
